import { useEffect, useRef } from 'react';
import { getDocumentScrollTop } from '../utils/scrollPosition';

/**
 * Side-Effect Hook fuer Infinite Scroll + Desktop Wheel-to-Refresh.
 * Bindet scroll/wheel Event-Listener und ruft onLoadMore/onDesktopRefresh auf.
 */
export const useInfiniteScroll = ({
  enabled = true,
  onLoadMore,
  onDesktopRefresh,
  canRefresh,
  isDesktop = false,
  threshold = 200
}) => {
  // Refs fuer stabile Referenzen in Event-Listenern
  const onLoadMoreRef = useRef(onLoadMore);
  const onDesktopRefreshRef = useRef(onDesktopRefresh);
  const canRefreshRef = useRef(canRefresh);

  useEffect(() => { onLoadMoreRef.current = onLoadMore; }, [onLoadMore]);
  useEffect(() => { onDesktopRefreshRef.current = onDesktopRefresh; }, [onDesktopRefresh]);
  useEffect(() => { canRefreshRef.current = canRefresh; }, [canRefresh]);

  // Scroll-Listener fuer automatisches Nachladen
  useEffect(() => {
    if (!enabled) return undefined;

    const handleScroll = () => {
      // Auf iOS WKWebView ist der echte Scroller der body (siehe scrollPosition.js).
      // scrollTop robust lesen, Hoehen konsistent vom selben Scroll-Element.
      const scroller = document.scrollingElement || document.documentElement;
      const scrollTop = getDocumentScrollTop();
      const scrollHeight = scroller.scrollHeight;
      const clientHeight = scroller.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        if (onLoadMoreRef.current) {
          onLoadMoreRef.current();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enabled, threshold]);

  // Desktop Wheel-to-Refresh
  useEffect(() => {
    if (!enabled || !isDesktop) return undefined;

    const handleWheel = (event) => {
      if (!canRefreshRef.current || !canRefreshRef.current()) return;
      if (getDocumentScrollTop() > 0) return;

      if (event.deltaY < -20 && onDesktopRefreshRef.current) {
        onDesktopRefreshRef.current();
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [enabled, isDesktop]);
};
